/**
 * Storage adapter for editor-specific storage access.
 *
 * Purpose: Abstracts away editor-specific storage patterns.
 * This allows the codebase to be more generic and reusable across different editor implementations.
 *
 * Key responsibilities:
 * - Get persistVeltMarks setting (for Slate, checks extension config)
 * - Determine if marks should be applied
 *
 * Dependencies:
 * - slate (ONLY place allowed to import Editor types for storage access)
 */
import { getEditorId } from '@/adapters/host/doc';
import { Constants } from '@/constants/common';
import { getEditorContext } from '@/core/registry';
import { Console } from '@/utils/console';
/**
 * Gets persistVeltMarks setting from storage.
 * For Slate, checks the extension config from registry.
 *
 * @param editor The editor instance
 * @param storageKey The storage key (unused for Slate, kept for API compatibility)
 * @returns persistVeltMarks boolean value (defaults to true)
 */
export const getPersistVeltMarks = (editor, storageKey = Constants.STORAGE_KEY) => {
    var _a;
    try {
        // Get editor ID first
        const editorId = getEditorId(editor);
        if (!editorId) {
            return true; // Default to true if editorId not found
        }
        // Get editor context from registry
        const context = getEditorContext(editorId);
        if (((_a = context === null || context === void 0 ? void 0 : context.config) === null || _a === void 0 ? void 0 : _a.persistVeltMarks) !== undefined) {
            return context.config.persistVeltMarks;
        }
        // Default to true if not configured
        return true;
    }
    catch (error) {
        Console.warn('[getPersistVeltMarks] Error checking persistVeltMarks, defaulting to true:', error);
        return true;
    }
};
/**
 * Determines if marks should be applied based on persistVeltMarks setting.
 * This is a unified adapter function that abstracts the mark application logic.
 *
 * @param editor The editor instance (typed as unknown to avoid importing editor types in feature modules)
 * @param storageKey The storage key (default: 'slateVeltComments')
 * @returns boolean indicating if marks should be applied
 *
 * @remarks
 * - For TipTap: checks persistVeltMarks setting (apply if FALSE)
 * - For Lexical: always returns true (always apply marks)
 * - For Slate: checks persistVeltMarks setting (apply if FALSE)
 * - Defaults to true if storage is unavailable
 */
export const shouldApplyMark = (editor, storageKey = Constants.STORAGE_KEY) => {
    try {
        const persistVeltMarks = getPersistVeltMarks(editor, storageKey);
        // Apply mark if persistVeltMarks is FALSE (matching legacy behavior)
        return persistVeltMarks === false;
    }
    catch (error) {
        Console.warn('[shouldApplyMark] Error checking persistVeltMarks, defaulting to true:', error);
        return true;
    }
};
